import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Brand";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/about", label: "About", id: "about" },
  { to: "/faqs", label: "FAQs", id: "faqs" },
  { to: "/blog", label: "Briefings", id: "blog" },
  { to: "/calculators", label: "Calculators", id: "calculators" },
  { to: "/equality-at-work", label: "Equality", id: "equality" },
  { to: "/contact", label: "Contact", id: "contact" },
];

const focusableSelector = "a[href], button:not([disabled])";

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const headerRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const previousPathRef = useRef(location.pathname);

  const closeMenu = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) {
      window.setTimeout(() => triggerRef.current?.focus(), 0);
    }
  }, []);

  useEffect(() => {
    if (previousPathRef.current === location.pathname) return;
    previousPathRef.current = location.pathname;
    closeMenu(false);
  }, [location.pathname, closeMenu]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.setTimeout(() => menuRef.current?.querySelector(focusableSelector)?.focus(), 0);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = [
        triggerRef.current,
        ...Array.from(menuRef.current?.querySelectorAll(focusableSelector) || []),
      ].filter(Boolean);
      if (!focusable.length) return;

      event.preventDefault();
      const currentIndex = focusable.indexOf(document.activeElement);
      const nextIndex = event.shiftKey
        ? (currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1)
        : (currentIndex === focusable.length - 1 ? 0 : currentIndex + 1);
      focusable[nextIndex].focus();
    };

    const onPointerDown = (event) => {
      if (!headerRef.current?.contains(event.target)) closeMenu();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open, closeMenu]);

  return (
    <header
      ref={headerRef}
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled || open ? "bg-ink/95 backdrop-blur-md border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-x flex h-16 sm:h-20 items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center text-paper" aria-label="Electra HR home">
          <Logo className="h-7 sm:h-8" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `link-underline whitespace-nowrap text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-pink" : "text-paper/80 hover:text-paper"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-book-call"
            className="group flex items-center gap-1.5 whitespace-nowrap bg-pink px-5 py-2.5 text-sm font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5"
          >
            Get a fixed quote
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          ref={triggerRef}
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-paper p-2"
          onClick={() => (open ? closeMenu() : setOpen(true))}
          aria-expanded={open}
          aria-controls="mobile-navigation"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <nav
          ref={menuRef}
          id="mobile-navigation"
          data-testid="mobile-menu"
          role="dialog"
          aria-modal="true"
          className="lg:hidden w-full max-h-[calc(100dvh-4rem)] sm:max-h-[calc(100dvh-5rem)] overflow-y-auto bg-ink border-t border-white/10 px-5 pb-8 pt-4"
          aria-label="Mobile"
        >
          <div className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                onClick={() => closeMenu(false)}
                data-testid={`nav-mobile-link-${l.id}`}
                className={({ isActive }) =>
                  `font-display whitespace-nowrap text-2xl font-medium py-3 border-b border-white/10 ${isActive ? "text-pink" : "text-paper"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => closeMenu()}
              data-testid="nav-mobile-cta-book-call"
              className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap bg-pink px-5 py-4 font-bold text-ink"
            >
              Get a fixed quote <ArrowUpRight size={18} />
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};
